'use client';

import {
  useDeepEqualMemo,
  useSubsequentEffect
} from '@carvana/merch-react-hooks';
import { datadogRum } from '@datadog/browser-rum';
import deepmerge from 'deepmerge';
import isEqual from 'lodash.isequal';
import omit from 'lodash.omit';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import type React from 'react';
import SortOption from '~/constants/common/SortOption';
import type {
  AppliedBooleanFilters,
  AppliedFilters,
  AppliedListFilters,
  AppliedRangeFilters,
  AvailabilityType,
  FinanceFilter,
  FinanceType,
  MakeType,
  ModelsKey,
  ModelType
} from '~/contracts/common';
import { DeepPartial } from '~/types';
import purgeEmpty from '~/utilities/purgeEmpty';
import {
  computeHasStaleRouterCache,
  decodeSavedUrlState
} from '~/utilities/routerCacheStaleness';

type ModelOrString = ModelType | string;

const removeFromMakes = <T extends ModelOrString>(
  listToCheck: T[],
  name: string
): T[] => {
  const isTrim = typeof listToCheck[0] === 'string';

  return listToCheck?.filter(
    (filter: any) => (isTrim ? filter : filter.name) !== name
  );
};

type AvailabilityPayload = {
  value?: AvailabilityType;
};

type BooleanPayload = {
  filterKey: keyof AppliedBooleanFilters;
  value: boolean;
};

type ListPayload = {
  filterKey: keyof Omit<AppliedListFilters, 'makes'>;
  value: string;
};

type MakesPayload =
  | {
      modelsKey?: never;
      name: string;
      parents?: never;
    }
  | {
      modelsKey: ModelsKey;
      name: string;
      parents: string[];
    };

export type RangePayload = {
  filterKey: keyof AppliedRangeFilters;
  value: Partial<{ max: number; min: number }>;
};

export type FinancePayload = {
  filterKey: keyof FinanceFilter;
  value: Partial<FinanceType>;
};

export type AppliedFiltersContextValue = {
  appliedFilters: Partial<AppliedFilters>;
  clearAllFilters: () => void;
  filtersOrSortDidChange: boolean;
  hasStaleRouterCache: boolean;
  incrementPopstateCount: () => void;
  isSortUserSelected: boolean;
  pageNumber: number;
  popstateCount: number;
  setAppliedFilters: React.Dispatch<
    React.SetStateAction<Partial<AppliedFilters>>
  >;
  setIsSortUserSelected: React.Dispatch<React.SetStateAction<boolean>>;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  setSortBy: React.Dispatch<React.SetStateAction<SortOption>>;
  setWasAllFiltersCleared: React.Dispatch<React.SetStateAction<boolean>>;
  sortBy: SortOption;
  updateAvailabilityFilter: (payload: AvailabilityPayload) => void;
  updateBooleanFilter: (payload: BooleanPayload) => void;
  updateListFilter: (payload: ListPayload) => void;
  updateMakes: (payload: MakesPayload) => void;
  updateRangeFilter: (payload: RangePayload | FinancePayload) => void;
  wasAllFiltersCleared: boolean;
};

export const defaultContext: AppliedFiltersContextValue = {
  appliedFilters: {},
  clearAllFilters() {},
  filtersOrSortDidChange: false,
  hasStaleRouterCache: false,
  incrementPopstateCount() {},
  isSortUserSelected: false,
  pageNumber: 1,
  popstateCount: 0,
  setAppliedFilters() {},
  setIsSortUserSelected() {},
  setPageNumber() {},
  setSortBy() {},
  setWasAllFiltersCleared() {},
  sortBy: SortOption.MostPopular,
  updateAvailabilityFilter() {},
  updateBooleanFilter() {},
  updateListFilter() {},
  updateMakes() {},
  updateRangeFilter() {},
  wasAllFiltersCleared: false
};

const AppliedFiltersContext =
  createContext<AppliedFiltersContextValue>(defaultContext);

export type OwnProps = {
  filters?: AppliedFilters;
  filtersOrSortDidChange?: boolean;
  pageNumber?: number;
  sortBy?: SortOption;
};

export function AppliedFiltersProvider(
  props: React.PropsWithChildren<OwnProps>
) {
  const [popstateCount, setPopstateCount] = useState(0);
  const incrementPopstateCount = useCallback(
    () => setPopstateCount(n => n + 1),
    []
  );

  // On SPA back-navigation, the App Router may serve a stale RSC keyed to
  // the last router-managed URL, not the pushState URL. useSyncUrl embeds
  // the SRP URL in window.history.state.srp on every pushState/replaceState,
  // so each history entry carries its own state. Read it here to initialize
  // filters, page, and sort from the correct URL instead of stale server props.
  const [savedUrlState] = useState(() => decodeSavedUrlState(props.filters));

  // True when the saved URL's state differs from what the server provided —
  // meaning the Router Cache served a stale RSC and we corrected it. When
  // the saved URL matches server props (e.g., /cars with no filters), the
  // RSC was correct and no forced re-fetch is needed.
  const hasStaleRouterCache = computeHasStaleRouterCache(savedUrlState, props);

  const [appliedFiltersInternal, setAppliedFiltersInternal] = useState<
    Partial<AppliedFilters>
  >(savedUrlState?.filters ?? props.filters ?? {});
  const [filtersOrSortDidChange, setFiltersOrSortDidChange] = useState(
    props.filtersOrSortDidChange ?? false
  );
  const [pageNumber, setPageNumber] = useState(
    savedUrlState?.page ?? props.pageNumber ?? 1
  );
  const [sortBy, setSortBy] = useState(
    savedUrlState?.sortBy ?? props.sortBy ?? SortOption.MostPopular
  );
  const [isSortUserSelected, setIsSortUserSelected] = useState(false);
  // Distinguishes "Clear All" from removing an individual filter tag that
  // happens to leave zero filters — both reach appliedFilters === {}, but
  // only Clear All represents starting a genuinely new search.
  const [wasAllFiltersCleared, setWasAllFiltersCleared] = useState(false);

  const appliedFilters = useDeepEqualMemo(appliedFiltersInternal);

  useSubsequentEffect(() => {
    setAppliedFiltersInternal(props.filters ?? {});
  }, [props.filters]);

  // Trigger update when filters or sort by change from their initial values
  useSubsequentEffect(() => {
    setFiltersOrSortDidChange(true);
  }, [appliedFilters, sortBy]);

  const setAppliedFilters = useCallback<
    AppliedFiltersContextValue['setAppliedFilters']
  >(stateOrFn => {
    const normalizeBodyStyles = (
      filters: Partial<AppliedFilters>
    ): Partial<AppliedFilters> => {
      if (!filters.bodyStyles) return filters;

      return {
        ...filters,
        bodyStyles: filters.bodyStyles.map(s => s.toLowerCase())
      };
    };

    if (typeof stateOrFn === 'function') {
      setAppliedFiltersInternal(previousAppliedFilters => {
        const nextAppliedFilters = normalizeBodyStyles(
          stateOrFn(previousAppliedFilters)
        );

        return isEqual(previousAppliedFilters, nextAppliedFilters)
          ? previousAppliedFilters
          : purgeEmpty(nextAppliedFilters) ?? {};
      });

      return;
    }

    const normalized = normalizeBodyStyles(stateOrFn);
    setAppliedFiltersInternal(previousAppliedFilters =>
      isEqual(previousAppliedFilters, normalized)
        ? previousAppliedFilters
        : purgeEmpty(normalized) ?? {}
    );
  }, []);

  const clearAllFilters = useCallback<
    AppliedFiltersContextValue['clearAllFilters']
  >(() => {
    setWasAllFiltersCleared(true);
    setAppliedFiltersInternal({});
  }, []);

  const updateAvailabilityFilter = useCallback<
    AppliedFiltersContextValue['updateAvailabilityFilter']
  >(
    ({ value }: AvailabilityPayload) =>
      setAppliedFiltersInternal(currentFilters => {
        const { earliestDayToDeliveryOrPickup: ignored, ...otherFilters } =
          currentFilters;
        const nextFilter = value
          ? { earliestDayToDeliveryOrPickup: value }
          : {};

        return (
          purgeEmpty({
            ...otherFilters,
            ...nextFilter
          }) ?? {}
        );
      }),
    []
  );

  const updateBooleanFilter = useCallback<
    AppliedFiltersContextValue['updateBooleanFilter']
  >(
    ({ filterKey, value }: BooleanPayload) =>
      setAppliedFiltersInternal(currentFilters => {
        const { [filterKey]: ignored, ...otherFilters } = currentFilters;
        const nextFilter = value ? { [filterKey]: value } : {};

        return (
          purgeEmpty({
            ...otherFilters,
            ...nextFilter
          }) ?? {}
        );
      }),
    []
  );

  const updateListFilter = useCallback<
    AppliedFiltersContextValue['updateListFilter']
  >(
    ({ filterKey, value }: ListPayload) =>
      setAppliedFiltersInternal(currentFilters => {
        const otherFilters = omit(currentFilters, [filterKey]);

        const filter = currentFilters[filterKey] ?? [];
        const shouldRemove = filter.includes(value);

        const nextValues = shouldRemove
          ? filter.filter(f => f !== value)
          : [...(filter ?? []), value];

        const shouldResetTruckFacets =
          !!currentFilters?.bodyStyles?.length &&
          filterKey === 'bodyStyles' &&
          value === 'pickup' &&
          ('towingCapacity' in otherFilters ||
            'truckCabTypes' in otherFilters ||
            'truckBedLengths' in otherFilters);

        const shouldRemoveFilter = nextValues.length === 0;
        const nextFilter = shouldRemoveFilter
          ? {}
          : { [filterKey]: nextValues };

        if (shouldResetTruckFacets) {
          return {
            ...omit(otherFilters, [
              'towingCapacity',
              'truckCabTypes',
              'truckBedLengths'
            ]),
            ...nextFilter
          };
        }

        // Reset size classes if the last body style is being deselected
        const shouldResetSizeClasses =
          filterKey === 'bodyStyles' &&
          currentFilters?.bodyStyles?.length === 1 &&
          currentFilters?.bodyStyles?.includes(value);

        if (shouldResetSizeClasses) {
          return {
            ...omit(otherFilters, ['sizeClasses']),
            ...nextFilter
          };
        }

        return (
          purgeEmpty({
            ...otherFilters,
            ...nextFilter
          }) ?? {}
        );
      }),
    []
  );

  const updateRangeFilter = useCallback<
    AppliedFiltersContextValue['updateRangeFilter']
  >(
    ({ filterKey, value }: RangePayload | FinancePayload) =>
      setAppliedFiltersInternal(currentFilters => {
        const { [filterKey]: ignored, ...otherFilters } = currentFilters;
        const shouldRemoveFilter = Object.keys(value).length === 0;
        const nextRange = shouldRemoveFilter
          ? {}
          : { [filterKey]: { ...value } };

        return (
          purgeEmpty({
            ...otherFilters,
            ...nextRange
          }) ?? {}
        );
      }),
    []
  );

  const updateMakes = useCallback<AppliedFiltersContextValue['updateMakes']>(
    ({ modelsKey, name, parents = [] }: MakesPayload) =>
      setAppliedFiltersInternal(currentFilters => {
        const [maybeMake, maybeModel] = parents;
        const makeName = maybeMake ?? name;
        const modelName = maybeModel ?? name;

        // When user selects a parent model, all non-parent models need to be unselected
        const makes =
          modelsKey === 'parentModels'
            ? currentFilters.makes?.reduce<MakeType[]>(
                (acc, make) => acc.concat({ ...make, models: [] }),
                []
              ) ?? []
            : currentFilters.makes ?? [];
        const makeToUpdate = makes.find(make => make.name === makeName);
        const modelsToUpdate = modelsKey ? makeToUpdate?.[modelsKey] ?? [] : [];

        switch (parents.length) {
          // Make
          case 0: {
            const shouldRemoveMake = makes.some(
              make => make?.name === makeName
            );

            const nextMakes = shouldRemoveMake
              ? removeFromMakes(makes, makeName)
              : makes.concat({ name: makeName });

            return purgeEmpty({ ...currentFilters, makes: nextMakes }) ?? {};
          }

          // Model
          case 1: {
            const shouldRemoveModel = modelsToUpdate.some(
              model => model?.name === modelName
            );

            const nextModels = shouldRemoveModel
              ? removeFromMakes(modelsToUpdate, modelName)
              : modelsToUpdate.concat({
                  name: modelName,
                  trims: []
                });

            const nextMakes = makes.map(make =>
              make.name === makeName
                ? {
                    ...make,
                    ...(modelsKey
                      ? {
                          name: makeName,
                          [modelsKey]: nextModels
                        }
                      : { name: makeName })
                  }
                : make
            );

            return purgeEmpty({ ...currentFilters, makes: nextMakes }) ?? {};
          }

          // Trim
          case 2: {
            const modelToUpdate = modelsToUpdate.find(
              model => model.name === modelName
            );
            const shouldRemoveTrim = modelToUpdate?.trims?.includes(name);

            const nextTrims = shouldRemoveTrim
              ? removeFromMakes(modelToUpdate?.trims ?? [], name)
              : (modelToUpdate?.trims ?? []).concat(name);

            const nextModels = modelsToUpdate.map(model =>
              model.name === modelName ? { ...model, trims: nextTrims } : model
            );

            const nextMakes = makes.map(make =>
              make.name === makeName
                ? {
                    ...make,
                    ...(modelsKey
                      ? {
                          name: makeName,
                          [modelsKey]: nextModels
                        }
                      : { name: makeName })
                  }
                : make
            );

            return purgeEmpty({ ...currentFilters, makes: nextMakes }) ?? {};
          }

          default:
            return currentFilters;
        }
      }),
    []
  );

  useEffect(() => {
    if (appliedFilters.finance) {
      datadogRum.addAction('Finance Filter Applied', {});
    }
  }, [appliedFilters]);

  const value = useMemo<AppliedFiltersContextValue>(
    () => ({
      appliedFilters,
      clearAllFilters,
      filtersOrSortDidChange,
      hasStaleRouterCache,
      incrementPopstateCount,
      isSortUserSelected,
      pageNumber,
      popstateCount,
      setAppliedFilters,
      setIsSortUserSelected,
      setPageNumber,
      setSortBy,
      setWasAllFiltersCleared,
      sortBy,
      updateAvailabilityFilter,
      updateBooleanFilter,
      updateListFilter,
      updateMakes,
      updateRangeFilter,
      wasAllFiltersCleared
    }),
    [
      appliedFilters,
      clearAllFilters,
      filtersOrSortDidChange,
      hasStaleRouterCache,
      incrementPopstateCount,
      isSortUserSelected,
      pageNumber,
      popstateCount,
      setAppliedFilters,
      sortBy,
      updateAvailabilityFilter,
      updateBooleanFilter,
      updateListFilter,
      updateMakes,
      updateRangeFilter,
      wasAllFiltersCleared
    ]
  );

  return (
    <AppliedFiltersContext.Provider value={value}>
      {props.children}
    </AppliedFiltersContext.Provider>
  );
}

AppliedFiltersProvider.displayName = 'AppliedFiltersProvider';

export default AppliedFiltersContext;

export const generateAppliedFiltersContext = (
  customState: DeepPartial<AppliedFiltersContextValue>
): AppliedFiltersContextValue =>
  deepmerge<
    AppliedFiltersContextValue,
    DeepPartial<AppliedFiltersContextValue>
  >(defaultContext, customState);

export function MockedAppliedFiltersContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{
  mockValue: DeepPartial<AppliedFiltersContextValue>;
}>) {
  return (
    <AppliedFiltersContext.Provider
      value={generateAppliedFiltersContext(mockValue)}
    >
      {children}
    </AppliedFiltersContext.Provider>
  );
}

export function useAppliedFiltersContext() {
  return useContext(AppliedFiltersContext);
}

export function useAppliedFilters(): Partial<AppliedFilters> {
  return useContext(AppliedFiltersContext).appliedFilters;
}
